import { useState, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  usePatchParticipantConsent,
} from "../basket-api";
import {
  handleApiError,
  showErrorToast,
  showSuccessToast,
} from "../error-handling";
import {
  prepareParticipantConsentPatch,
  type ConsentFormValues,
} from "../consent-schema";
import type { CheckoutParticipant } from "../checkout-participant";

/**
 * Hook for managing participant editing and consent form completion
 */
export function useParticipantEditing(
  checkoutId: string | null,
  refetchParticipants: () => void,
) {
  const queryClient = useQueryClient();
  const [editingParticipant, setEditingParticipant] =
    useState<CheckoutParticipant | null>(null);
  const patchParticipantConsent = usePatchParticipantConsent();

  useEffect(() => {
    if (editingParticipant) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [editingParticipant]);

  const handleEditParticipant = (participant: CheckoutParticipant) => {
    setEditingParticipant(participant);
  };

  const handleCloseEditModal = () => {
    setEditingParticipant(null);
  };

  const saveParticipantConsent = async (formData: Partial<ConsentFormValues>) => {
    if (!checkoutId || !editingParticipant) return;

    const body = prepareParticipantConsentPatch(
      editingParticipant,
      formData,
      {
        requiresConsentForm: editingParticipant.requiresConsentForm,
        requiresPhotoConsent: editingParticipant.requiresPhotoConsent,
      },
    );

    await patchParticipantConsent.mutateAsync({
      params: { path: { checkoutId } },
      body,
    });

    await queryClient.invalidateQueries({
      queryKey: ["get", "/api/public/checkouts/{checkoutId}/participants"],
    });
    refetchParticipants();
  };

  const handleStepSave = async (formData: Partial<ConsentFormValues>) => {
    try {
      await saveParticipantConsent(formData);
    } catch (error) {
      const apiError = handleApiError(error);
      showErrorToast(apiError);
      throw error;
    }
  };

  const handleConsentFormComplete = async (formData: ConsentFormValues) => {
    if (!checkoutId || !editingParticipant) return;

    try {
      await saveParticipantConsent(formData);
      setEditingParticipant(null);
      showSuccessToast("Consent forms completed successfully for all events");
    } catch (error) {
      const apiError = handleApiError(error);
      showErrorToast(apiError);
    }
  };

  return {
    editingParticipant,
    handleEditParticipant,
    handleCloseEditModal,
    handleStepSave,
    handleConsentFormComplete,
    isSaving: patchParticipantConsent.isPending,
  };
}
